import { Box, Flex, FormLabel, Input, Select, Text } from '@chakra-ui/react';
import React, { useState } from 'react';

interface Props {
	selectedCourse: string;
	selectedDomain: string;
	selectedSpec: string;
	selectedYear: string;
	setSelectedCourse: (course: string) => void;
	setSelectedDomain: (domain: string) => void;
	setSelectedSpec: (spec: string) => void;
	setSelectedYear: (year: string) => void;
}
export const CoreSectorPossibleRoles = {
	'Computer Science': ['Network Administrator', 'DevOps Engineer'],
	'Electronics & Communication': ['Electronics Engineer', 'Audio/Video Engineer'],
	'Electrical Engineering': ['Electrical Engineer', 'Electrical Design Engineer'],
	'Mechanical Engineering': ['Mechanical Engineer', 'Manufacturing Engineer'],
	'Civil Engineering': ['Civil Engineer', 'Urban Planner'],
	'Aeronautical Engineering': ['Aeronautical Engineer', 'Flight Test Engineer'],
	Biotechnology: ['Biotechnologist', 'Quality Control Analyst'],
	'Machine Learning & AI': ['Machine Learning Engineer', 'Computer Vision Engineer'],
	Telecommunication: ['Telecom Engineer', 'Network Security Analyst'],
	'Data Science': ['Data Scientist', 'Business Analyst'],
	Chemistry: ['Chemist', 'Research Scientist'],
	Mathematics: ['Statistician', 'Quantitative Analyst'],
	Economics: ['Financial Analyst', 'Market Researcher'],
	Geology: ['Geologist', 'Mineralogist'],
	Agriculture: ['Agricultural Scientist', 'Agricultural Economist'],
	Biology: ['Biologist', 'Geneticist'],
	Microbiology: ['Microbiologist', 'Research Scientist'],
	Psychology: ['Clinical Psychologist', 'Counseling Psychologist'],
	Anthropology: ['Anthropologist', 'Archaeologist'],
	Physics: ['Physicist', 'Experimental Physicist'],
	'Information Technology': ['IT Manager', 'Systems Administrator'],
	'Computer Graphics': ['Graphic Designer', 'UX/UI Designer'],
	'Management Information Systems': ['Business Analyst', 'Systems Analyst'],
	'Computer Networks': ['Network Engineer', 'Network Administrator'],
	'Database Management Systems (DBMS)': ['Database Administrator', 'Data Analyst'],
	'Software Engineering': ['DevOps Engineer'],
	'Cloud Computing': ['Cloud Engineer', 'Cloud Architect'],
	Java: ['DevOps Engineer'],
	'Artificial Intelligence': ['AI Engineer', 'AI Research Scientist'],
	'Cyber Security': ['Cybersecurity Analyst', 'Security Engineer'],
	'Data Analytics': ['Data Analyst', 'Business Intelligence Analyst'],
	'Systems Management': ['Systems Manager', 'IT Operations Manager'],
	'Systems Development': ['Systems Analyst', 'Systems Architect'],
	'Computer Engineering': ['DevOps Engineer'],
	'Automobile Engineering': ['Automobile Engineer', 'Automotive Designer'],
	'Aerospace Engineering': ['Aerospace Engineer', 'Spacecraft Engineer'],
	'Food Engineering': ['Food Engineer', 'Food Safety Specialist'],
	'Marine Engineering': ['Marine Engineer', 'Naval Architect'],
	'Industrial Engineering': ['Industrial Engineer', 'Operations Manager'],
	'Mining Engineering': ['Mine Safety Engineer', 'Mineral Processing Engineer'],
	'Human Resources': ['Human Resources Manager', 'HR Generalist'],
	'Hotel Management': ['Hotel Manager', 'Front Office Manager'],
};
const CollegeInformation = ({
	selectedCourse,
	selectedDomain,
	selectedSpec,
	selectedYear,
	setSelectedCourse,
	setSelectedDomain,
	setSelectedSpec,
	setSelectedYear,
}: Props) => {
	type Domain = 'domain1' | 'domain2' | 'domain3';
	type Course = 'course1' | 'c2';
	type Specialization = string;

	interface CourseData {
		[key: string]: Specialization[];
	}

	interface DomainData {
		[key: string]: CourseData;
	}
	const obj = [
		{
			Engineering: {
				BTech: [
					'Computer Science',
					'Electronics & Communication',
					'Electrical Engineering',
					'Mechanical Engineering',
					'Civil Engineering',
					'Aeronautical Engineering',
					'Biotechnology',
				],
				BSc: ['Chemistry', 'Mathematics', 'Economics', 'Geology', 'Agriculture', 'Biology', 'Microbiology'],
				BCA: ['Informations Technology', 'Computer Graphics', 'Management Information Systems', 'Computer Networks', 'DBMS', 'Software Engineering'],
				BE: [
					'Computer Engineering',
					'Automobile Engineering',
					'Civil Engineering',
					'Electrical Engineering',
					'Aerospace Engineering',
					'Food Engineering',
					'Marine Engineering',
					'Industrial Engineering',
					'Mining Engineering',
				],
				MCA: [
					'Data Analytics',
					'Data Science',
					'Artificial Intelligence',
					'Cyber Security',
					'Cloud Computing',
					'Management Information Systems',
					'Systems Management',
					'Systems Development',
				],
				Other: [
					'Machine Learning',
					'AI',
					'Telecommunication',
					'Data Science',
					'Science',
					'Psychology',
					'Anthropology',
					'Physics',
					'Cloud Computing',
					'Java',
					'Artificial Intelligence',
					'Cyber Security',
					'Data Science',
				],
			},
		},
		{
			Management: {
				BBA: [
					'Human Resources',
					'Hotel Management',
					'Operations Management',
					'Management Studies',
					'Marketing',
					'Business Analytics',
					'Finance Management',
					'Travel & Tourism',
					'Supply Chain Management',
					'Business Administration',
					'Economics',
					'IT/Computer Application',
					'Banking',
					'Logistics Management',
					'International Business',
					'Entrepreneurship',
				],
				MBA: [
					'Finance',
					'Operations Management',
					'Human Resources',
					'Financial Management',
					'Rural Management',
					'Health Administration',
					'Marketing, Advertising',
					'International Business',
					'Data Analytics',
					'Entrepreneurship',
					'Business Analytics',
				],
			},
		},
		{
			Humanities: {
				BA: [
					'Psychology',
					'Political Science',
					'English',
					'Geography',
					'Economics',
					'History',
					'Anthropology',
					'Philosophy',
					'Sociology',
					'Art',
					'Linguistics',
					'Media Studies',
					'Marketing, Advertising',
					'Mass Communication',
				],
				MA: [
					'Social Sciences',
					'Political Science',
					'International Relations',
					'Cultural Studies',
					'Development Studies',
					'Sociology',
					'History',
					'Communication Studies',
					'Marketing, Advertising',
					'Philosophy',
					'Religious Studies',
				],
			},
		},
		{
			Other: {
				Other: [
					'Other'
				]
			},
		},
	];

	const years = Array.from({ length: 11 }, (_, i) => 2020 + i);

	// Get available courses based on the selected domain
	// @ts-ignore
	const availableCourses: any = selectedDomain && obj.find((item: any) => Object.keys(item)[0] === selectedDomain)?.[selectedDomain];

	// Get available specializations based on the selected course
	const availableSpecializations = selectedCourse && availableCourses && availableCourses[selectedCourse];

	// Handle domain select change
	const handleDomainChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedDomain(event.target.value);
		setSelectedCourse(''); // Clear the selected course when the domain changes
		setSelectedSpec(''); // Clear the selected specialization when the domain changes
	};

	// Handle course select change
	const handleCourseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedCourse(event.target.value);
		setSelectedSpec(''); // Clear the selected specialization when the course changes
	};

	// Handle specialization select change
	const handleSpecChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedSpec(event.target.value);
	};
	const handleSpecYearOfGraduation = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedYear(event.target.value);
	};

	return (
		<Flex alignItems={'flex-end'} justifyContent={'center'}>
			<Flex w="100%" mb={'6'} flexDirection={'column'}>
				<Box>
					<Text textStyle="body2-md">Select Your Domain</Text>
					<Select
						variant="outline"
						h={'48px'}
						mt="2"
						rounded={'8px'}
						border={'1px solid'}
						borderColor={'gray.300'}
						color={selectedDomain ? 'black' : 'gray.500'}
						onChange={handleDomainChange}
						value={selectedDomain}
						iconColor="black"
					>
						<option value="" style={{ color: 'black' }}>
							Select your Domain
						</option>
						{obj.map((item, index) => (
							<option key={index} value={Object.keys(item)[0]} style={{ color: 'black' }}>
								{Object.keys(item)[0]}
							</option>
						))}
					</Select>
				</Box>
				<Box mt={'3'}>
					<Text textStyle="body2-md">Which course are you enrolled in?</Text>
					<Select
						mt="2"
						rounded={'8px'}
						border={'1px solid'}
						borderColor={'gray.300'}
						h={'48px'}
						id="courseSelect"
						iconColor="black"
						color={selectedCourse ? 'black' : 'gray.500'}
						onChange={handleCourseChange}
						value={selectedCourse}
					>
						<option value="">Select course</option>
						{availableCourses &&
							Object.keys(availableCourses).map((course, index) => (
								<option key={index} value={course} style={{ color: 'black' }}>
									{course}
								</option>
							))}
					</Select>
				</Box>
				<Box mt={'3'}>
					<Text textStyle="body2-md">Choose your course specialisation</Text>
					<Select
						mt={'2'}
						h={'48px'}
						rounded={'8px'}
						border={'1px solid'}
						borderColor={'gray.300'}
						iconColor="black"
						color={selectedSpec ? 'black' : 'gray.500'}
						id="specSelect"
						onChange={handleSpecChange}
						value={selectedSpec}
					>
						<option value="">Select course specialization</option>
						{availableSpecializations &&
							availableSpecializations.map((spec: any, index: any) => (
								<option key={index} value={spec} style={{ color: 'black' }}>
									{spec}
								</option>
							))}
					</Select>
				</Box>
				<Box mt={'3'}>
					<Text textStyle="body2-md">Year of graduation</Text>

					<Select
						h={'48px'}
						mt={'2'}
						rounded={'8px'}
						border={'1px solid'}
						borderColor={'gray.300'}
						iconColor="black"
						color={selectedSpec ? 'black' : 'gray.500'}
						id="specSelect"
						onChange={handleSpecYearOfGraduation}
						value={selectedYear}
					>
						<option value="">Select year of graduation</option>
						{years.map(year => (
							<option key={year} value={year}>
								{year}
							</option>
						))}
					</Select>
				</Box>
			</Flex>
		</Flex>
	);
};

export default CollegeInformation;
